import React from 'react';
import { Clock, AlertCircle, X, Mail, Phone, LogOut } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const PendingStatusPopup = ({ isOpen, onClose, onLogout, theme, currentThemeName }) => {
  const { t } = useLanguage();
  if (!isOpen) return null;

  const isDark = currentThemeName === 'dark' || currentThemeName === 'midnight';

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    }
    onClose();
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      backdropFilter: 'blur(10px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '20px'
    }}>
      <div style={{
        background: isDark 
          ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
          : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        borderRadius: '24px',
        padding: '40px',
        maxWidth: '500px',
        width: '100%',
        border: isDark 
          ? '1px solid rgba(255, 255, 255, 0.1)'
          : '1px solid rgba(0, 0, 0, 0.1)',
        boxShadow: isDark
          ? '0 25px 50px rgba(0, 0, 0, 0.5)'
          : '0 25px 50px rgba(0, 0, 0, 0.15)',
        position: 'relative'
      }}>
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            background: 'none',
            border: 'none',
            color: isDark ? '#ffffff' : '#000000',
            cursor: 'pointer',
            padding: '8px',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
          }}
        >
          <X size={20} />
        </button>

        {/* Icon */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '24px'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #f59e0b, #d97706)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 10px 30px rgba(245, 158, 11, 0.3)'
          }}>
            <Clock size={40} color="white" />
          </div>
        </div>

        {/* Title */}
        <h2 style={{
          fontSize: '28px',
          fontWeight: '700',
          color: isDark ? '#ffffff' : '#000000',
          textAlign: 'center',
          margin: '0 0 16px 0',
          lineHeight: '1.2'
        }}>
          {t('pending.title')}
        </h2>

        {/* Description */}
        <p style={{
          fontSize: '16px',
          color: isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.7)',
          textAlign: 'center',
          margin: '0 0 32px 0',
          lineHeight: '1.6'
        }}>
          {t('pending.description')}
        </p>

        {/* Info box */}
        <div style={{
          background: isDark 
            ? 'rgba(245, 158, 11, 0.1)'
            : 'rgba(245, 158, 11, 0.05)',
          border: isDark
            ? '1px solid rgba(245, 158, 11, 0.3)'
            : '1px solid rgba(245, 158, 11, 0.2)',
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '32px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '12px'
          }}>
            <AlertCircle size={20} color="#f59e0b" />
            <span style={{
              fontSize: '16px',
              fontWeight: '600',
              color: isDark ? '#ffffff' : '#000000'
            }}>
              {t('pending.whatHappensNext')}
            </span>
          </div>
          <ul style={{
            margin: 0,
            paddingLeft: '20px',
            color: isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.7)',
            fontSize: '14px',
            lineHeight: '1.6'
          }}>
            <li>{t('pending.bullet1')}</li>
            <li>{t('pending.bullet2')}</li>
            <li>{t('pending.bullet3')}</li>
            <li>{t('pending.bullet4')}</li>
          </ul>
        </div>

        {/* Contact info */}
        <div style={{
          textAlign: 'center',
          padding: '20px',
          background: isDark 
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(0, 0, 0, 0.05)',
          borderRadius: '12px',
          marginBottom: '24px'
        }}>
          <p style={{
            fontSize: '14px',
            color: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
            margin: '0 0 12px 0'
          }}>
            {t('pending.contactSupport')}
          </p>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '24px',
            flexWrap: 'wrap'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: isDark ? '#ffffff' : '#000000',
              fontSize: '14px'
            }}>
              <Mail size={16} />
              <span>support@oaklandai.com</span>
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: isDark ? '#ffffff' : '#000000',
              fontSize: '14px'
            }}>
              <Phone size={16} />
              <span>(555) 123-4567</span>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={handleLogout}
            style={{
              padding: '12px 24px',
              borderRadius: '12px',
              border: 'none',
              background: 'linear-gradient(135deg, #ef4444, #dc2626)',
              color: '#ffffff',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'linear-gradient(135deg, #dc2626, #b91c1c)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'linear-gradient(135deg, #ef4444, #dc2626)';
            }}
          >
            <LogOut size={16} />
            {t('pending.logout')}
          </button>
          <button
            onClick={onClose}
            style={{
              padding: '12px 24px',
              borderRadius: '12px',
              border: 'none',
              background: isDark 
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.1)',
              color: isDark ? '#ffffff' : '#000000',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = isDark 
                ? 'rgba(255, 255, 255, 0.2)'
                : 'rgba(0, 0, 0, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = isDark 
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.1)';
            }}
          >
            {t('pending.close')}
          </button>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '12px 24px',
              borderRadius: '12px',
              border: 'none',
              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
              color: '#ffffff',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'linear-gradient(135deg, #2563eb, #1e40af)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'linear-gradient(135deg, #3b82f6, #1d4ed8)';
            }}
          >
            {t('pending.refreshStatus')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PendingStatusPopup;
